import React, { useEffect, useState } from "react";
import { wisdomPath } from "../lib/api";
import { useNavigate } from "react-router-dom";
import { BookOpen, ArrowRight, X } from "lucide-react";
import { toast } from "sonner";
import SaveWisdomButton from "./SaveWisdomButton";

export default function WisdomPathCard() {
  const [path, setPath] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    wisdomPath.latest().then((p) => {
      if (p && p.id) setPath(p);
    }).catch(() => {});
  }, []);

  if (!path) return null;

  const cont = async () => {
    setExpanded(true);
  };

  const dismiss = async () => {
    try {
      await wisdomPath.consume(path.id);
      setPath(null);
    } catch {
      toast.error("Could not dismiss");
    }
  };

  return (
    <section className="fade-in-up stagger-1" data-testid="wisdom-path-card">
      <div className="rounded-3xl border border-[#D6C7A1]/60 bg-[#F5EBD9]/60 p-6 shadow-[0_4px_12px_rgba(0,0,0,0.04)]">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.16em] font-semibold text-[#8C7A4F]">
              <BookOpen size={12} /> From I Need a Proverb
            </span>
          </div>
          <button
            onClick={dismiss}
            data-testid="wisdom-dismiss"
            className="text-[#6B6B6B] hover:text-[#2E2E2E] p-1"
            aria-label="Dismiss"
          >
            <X size={16} />
          </button>
        </div>

        <h2 className="font-display text-2xl text-[#2E2E2E] mb-2">
          Continue Your Wisdom Path
        </h2>
        {path.title && (
          <p className="font-display text-lg text-[#4F6F52] mb-3">{path.title}</p>
        )}

        {path.selected_issues?.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-3">
            {path.selected_issues.map((s, i) => (
              <span key={i} className="text-xs rounded-full px-2.5 py-1 bg-white/70 border border-[#DCE5DB] text-[#2E2E2E]">
                {s}
              </span>
            ))}
          </div>
        )}

        {path.scripture_references?.length > 0 && (
          <p className="text-sm text-[#6B6B6B] mb-2">
            <span className="font-medium text-[#2E2E2E]">Scripture:</span> {path.scripture_references.join(" · ")}
          </p>
        )}

        {path.guidance_summary && !expanded && (
          <p className="text-[15px] text-[#2E2E2E] leading-relaxed mb-4 line-clamp-3">
            {path.guidance_summary}
          </p>
        )}

        {expanded && (
          <div className="space-y-3 mb-4 fade-in-up">
            {path.verse_text && (
              <div className="rounded-2xl bg-white border border-[#DCE5DB] p-4">
                <div className="text-xs uppercase tracking-[0.14em] text-[#7FA7B0] font-semibold mb-1.5">Scripture</div>
                <blockquote className="italic text-[15px] leading-[1.8] text-[#2E2E2E]">
                  {/* Do not rewrite transferred Scripture */}
                  {path.verse_text}
                </blockquote>
                {path.scripture_references?.length > 0 && (
                  <div className="text-xs text-[#6B6B6B] mt-2">
                    {path.scripture_references.join(" · ")}
                  </div>
                )}
              </div>
            )}
            {path.explanation && (
              <div className="rounded-2xl bg-[#EEF3EC] border border-[#DCE5DB] p-4">
                <div className="text-xs uppercase tracking-[0.14em] text-[#4F6F52] font-semibold mb-1.5">Explanation</div>
                <p className="text-[15px] leading-[1.75] text-[#2E2E2E]">{path.explanation}</p>
              </div>
            )}
            {path.practical_guidance && (
              <div className="rounded-2xl bg-[#E1ECDD] border border-[#DCE5DB] p-4">
                <div className="text-xs uppercase tracking-[0.14em] text-[#4F6F52] font-semibold mb-1.5">Practical Application</div>
                <p className="text-[15px] leading-[1.75] text-[#2E2E2E]">{path.practical_guidance}</p>
              </div>
            )}
            {path.prayer && (
              <div className="rounded-2xl bg-white border border-[#DCE5DB] p-4">
                <div className="text-xs uppercase tracking-[0.14em] text-[#8C7A4F] font-semibold mb-1.5">Prayer</div>
                <p className="text-[15px] leading-[1.85] italic text-[#2E2E2E]">{path.prayer}</p>
              </div>
            )}
          </div>
        )}

        {!expanded ? (
          <div className="flex flex-wrap gap-2">
            <button
              onClick={cont}
              data-testid="wisdom-continue"
              className="inline-flex items-center gap-2 bg-[#4F6F52] hover:bg-[#3F5A42] text-white rounded-full px-5 py-2.5 text-sm font-medium transition shadow-[0_4px_14px_rgba(79,111,82,0.18)]"
            >
              Continue in LifeGuide <ArrowRight size={14} />
            </button>
            <SaveWisdomButton
              kind="wisdom_path"
              referenceId={path.id}
              title={path.title || "Wisdom Path"}
              body={[
                path.guidance_summary,
                path.verse_text ? `Scripture: ${path.verse_text}` : null,
                path.explanation ? `Explanation: ${path.explanation}` : null,
                path.practical_guidance ? `Practical: ${path.practical_guidance}` : null,
                path.prayer ? `Prayer: ${path.prayer}` : null,
              ].filter(Boolean).join("\n\n")}
              meta={{ scripture_reference: (path.scripture_references || []).join(" · ") || null }}
              testid="wisdom-path-save"
            />
          </div>
        ) : (
          <div className="flex flex-wrap gap-2">
            <button
              onClick={dismiss}
              data-testid="wisdom-close"
              className="inline-flex items-center gap-2 bg-white border border-[#DCE5DB] hover:bg-[#EEF3EC] text-[#2E2E2E] rounded-full px-5 py-2.5 text-sm font-medium transition"
            >
              Close this path
            </button>
            <SaveWisdomButton
              kind="wisdom_path"
              referenceId={path.id}
              title={path.title || "Wisdom Path"}
              body={[
                path.guidance_summary,
                path.verse_text ? `Scripture: ${path.verse_text}` : null,
                path.explanation ? `Explanation: ${path.explanation}` : null,
                path.practical_guidance ? `Practical: ${path.practical_guidance}` : null,
                path.prayer ? `Prayer: ${path.prayer}` : null,
              ].filter(Boolean).join("\n\n")}
              meta={{ scripture_reference: (path.scripture_references || []).join(" · ") || null }}
              testid="wisdom-path-save-expanded"
            />
          </div>
        )}
      </div>
    </section>
  );
}
